import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { accountApi } from "@/resources/account";
import { RoutePath } from "@/routes";

/**
 * AUT-399 follow-up. The Admin Panel entry used to be a `<button>` that fetched an SSO link and
 * `window.open`ed it, so it opened a new tab on a plain click but could never be cmd/middle-clicked
 * or opened from the context menu -- there was no `href` to act on.
 *
 * The link cannot simply be fetched up front and written into an `href`: `AdminSsoController::link`
 * mints a token that lives **two minutes** (`Cache::put(..., now()->addMinutes(2))`, and the signed
 * route expires with it) and is **single use** (`consume` calls `Cache::pull`). A link written when
 * the sidebar rendered would be dead by the time most people clicked it, and would work exactly
 * once if it weren't.
 *
 * So the sidebar links here instead, and this page mints the token at the moment of navigation and
 * forwards immediately -- comfortably inside the two minutes, consumed once. The security model is
 * unchanged; only the moment of minting moves.
 */
export default function AdminPanelRedirect() {
  const navigate = useNavigate();
  const { mutate: getAdminSsoUrl } = accountApi.useGetAdminSsoUrl();
  const [failed, setFailed] = useState(false);

  // React 18 StrictMode mounts effects twice in development. The token is single-use, so a second
  // mint would leave an unused one behind and, worse, race the redirect -- ask only once.
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;

    getAdminSsoUrl(
      {},
      {
        // `replace`, not `assign`: this page is a staging post, so Back should return to wherever
        // the user came from rather than landing here and minting another token.
        onSuccess: ({ url }) => window.location.replace(url),
        onError: () => setFailed(true),
      },
    );
  }, [getAdminSsoUrl]);

  if (failed) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <Shield className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-base font-semibold text-foreground">Could not open the Admin Panel</p>
          <p className="text-sm text-muted-foreground">
            The sign-in link could not be created. Please try again.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => window.location.reload()}>Try again</Button>
          <Button variant="outline" onClick={() => navigate(RoutePath.Home)}>
            Go back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center gap-3"
      role="status"
      aria-live="polite"
    >
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary"
        aria-hidden="true"
      />
      <p className="text-sm text-muted-foreground">Opening the Admin Panel…</p>
    </div>
  );
}
